import { useState, useEffect } from 'react'
import { DEFAULT_ZONE, todayIn } from './timezone'
import { recall, remember, onRefresh } from '../cache'

// Date range, timezone and report fetch shared by the B-book report screens: keeps the last report on screen while a new range loads, and exposes a retry.
export const useReport = (fetchReport, user, defaultDays = 30) => {
  const [zone, setZone] = useState(DEFAULT_ZONE)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = todayIn(DEFAULT_ZONE) // Paris's today, even for a viewer on the other side of the world
    return {
      date1: { startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() - defaultDays), endDate: today },
      date2: { startDate: null, endDate: null },
    }
  })
  // the last report for this range paints at once, the fresh one replaces it when it lands
  const cacheKey = () => `${fetchReport.name}:${selectedDate.date1.startDate}:${selectedDate.date1.endDate}:${zone}`
  const [report, setReport] = useState(() => recall(cacheKey()) ?? null)
  const [loading, setLoading] = useState(() => recall(cacheKey()) === undefined)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => onRefresh(() => setAttempt((n) => n + 1)), []) // the refresh button

  useEffect(() => {
    const { startDate, endDate } = selectedDate.date1
    if (!startDate || !endDate) return // the picker clears the end while a new range is being chosen
    let active = true
    const cached = recall(cacheKey())
    if (cached) setReport(cached) // a range seen before shows its last figures while the new ones load
    const load = async () => {
      try {
        setLoading(true)
        const data = await fetchReport(user, { startDate, endDate }, zone)
        if (active) { setReport(remember(cacheKey(), data)); setFailed(false) }
      } catch (e) {
        if (active) setFailed(true)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false } // a slow answer for an old range must not overwrite the new one
  }, [selectedDate, zone, attempt])

  return { selectedDate, setSelectedDate, zone, setZone, report, loading, failed, retry: () => setAttempt((n) => n + 1) }
}
